import { Actions as BAMBOO_ACTIONS } from "@/integrations/bamboo-hr/store/bambooActions";
import { Actions as SMART_PENSION_ACTIONS } from "@/integrations/smartPension/store/smartPensionActions";
import { Actions as EMPLOYEE_ACTIONS } from "@/redux/actions/employeeActions";
import { Actions as INTEGRATION_ACTIONS } from "@/redux/actions/integrationActions";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowLeftIcon } from "@/icons/solid";
import BuddySelector from "buddy/selector";
import { camelCase, orderBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

const importEmployeeIntegrations = ["Bamboo HR", "Smart Pension"];

const ImportEmployeeModal = ({
	modalStatus,
	closeModal,
	selectIntegration,
	employeeList,
	integrationList,
	getEmployeeLists,
	ignoreEmployee,
	importEmployee,
	openExcelImportModal,
	jurisdiction,
}) => {
	const nonIntegratedImports = [
		{
			label: "Excel Import",
			value: "Excel Import",
			description: "Upload an Excel file to import employees (.xlsx, .xls)",
			icon: "ListBulletIcon",
			onSelect: openExcelImportModal,
		},
		{
			label: "P45/New Starter Scan",
			value: "P45/New Starter Scan",
			jurisdiction: ["UK"],
			icon: "PhotoIcon",
			isComingSoon: true,
			isDisabled: true,
			description: "Scan P45s and New Starter forms to import employees (.png, .jpg, .jpeg, .pdf)",
		},
		{
			label: "FPS XML Upload",
			value: "FPS XML Upload",
			jurisdiction: ["UK"],
			icon: "CodeBracketIcon",
			description: "Upload an FPS XML file to import employees (.xml)",
			onSelect: openExcelImportModal,
		},
	];

	const integrations = useMemo(() => {
		const modalOptions = [...nonIntegratedImports].filter(
			(e) => !e.jurisdiction || e.jurisdiction?.includes(jurisdiction),
		);
		if (integrationList?.filter((i) => !i.isDisabled)?.length > 0) modalOptions.push(...integrationList);

		const toReturn = [
			{
				label: "Integration",
				options: modalOptions.filter(({ integrated, isDisabled }) => integrated && !isDisabled),
			},
			{
				label: "Buddy",
				options: modalOptions.filter(({ integrated, isDisabled }) => !integrated && !isDisabled),
			},
			{
				label: "Coming Soon",
				options: modalOptions.filter(({ isComingSoon }) => isComingSoon),
			},
			{
				label: "Not Supported",
				options: modalOptions.filter(
					({ integrated, isDisabled, isComingSoon }) => integrated && isDisabled && !isComingSoon,
				),
			},
		];

		return toReturn;
	}, [JSON.stringify(integrationList), jurisdiction]);

	const selectedIntegration = useMemo(
		() => integrationList?.find((item) => item.value === modalStatus),
		[JSON.stringify(integrationList), modalStatus],
	);
	const selectedIntegrationName = selectedIntegration?.label;

	const numberOfOptions = useMemo(() => {
		return integrations?.flatMap(({ options }) => options)?.filter((i) => !i.isDisabled).length;
	}, [JSON.stringify(integrations)]);

	useEffect(() => {
		if (numberOfOptions === 1 && modalStatus) openExcelImportModal(nonIntegratedImports[0].value);
	}, [numberOfOptions, modalStatus]);

	const [selectedEmployees, setSelectedEmployeeList] = useState({});
	const [selectedEmployeeArray, selectedEmployeesLength] = useMemo(() => {
		const array = Object.keys(selectedEmployees);
		return [array, array.length];
	}, [selectedEmployees]);

	useEffect(() => {
		if (typeof modalStatus === "boolean") setSelectedEmployeeList({});
		if (typeof modalStatus === "string") getEmployeeLists?.[modalStatus]?.();
	}, [modalStatus]);

	const ignoreEmployees = () => {
		ignoreEmployee?.[modalStatus]?.(selectedEmployeeArray);
		setSelectedEmployeeList({});
	};
	const importEmployees = () => {
		importEmployee?.[modalStatus]?.(selectedEmployeeArray);
		setSelectedEmployeeList({});
	};

	if (!modalStatus) return null;

	return (
		<div
			className="modal"
			tabIndex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
		>
			<div
				className="modal-dialog"
				style={{ width: "50%" }}
			>
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div
							className="block-header bg-primary-dark"
							style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
						>
							<ul className="block-options">
								<li>
									<button
										type="button"
										onClick={() => {
											closeModal();
										}}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3 className="block-title">
								Import{selectedIntegration && ` ${selectedIntegrationName}`} Employees
							</h3>
						</div>
						<div
							className="block-content"
							style={{ paddingBottom: "2rem" }}
						>
							{employeeList ? (
								<>
									<table className="js-table-sections table table-hover">
										<thead>
											<tr>
												<th>Name</th>
												<th width="5%">
													{employeeList?.length ? (
														<label className="css-input css-checkbox css-checkbox-success">
															<input
																type="checkbox"
																id={`checkbox-all`}
																checked={
																	selectedEmployeesLength == employeeList?.length
																}
																onChange={(e) => {
																	const newObject = { ...selectedEmployees };
																	employeeList?.map((item) => {
																		if (!e.target.checked)
																			delete newObject[item.id];
																		else newObject[item.id] = true;
																	});
																	setSelectedEmployeeList(newObject);
																}}
															/>
															<span />
															Select All
														</label>
													) : null}
												</th>
											</tr>
										</thead>
										<tbody>
											{employeeList?.map((employee) => {
												return (
													<tr key={employee.id}>
														<td style={{ width: "60%" }}>
															<label htmlFor={`checkbox-${employee.id}`}>
																{`(${employee.id}) - ${
																	employee.title ? `${employee.title} ` : ""
																}${employee.firstName} ${employee.lastName}`}
															</label>
														</td>
														<td>
															<label className="css-input css-checkbox css-checkbox-success">
																<input
																	type="checkbox"
																	id={`checkbox-${employee.id}`}
																	checked={Object.keys({
																		...selectedEmployees,
																	}).includes(employee.id)}
																	onChange={(e) => {
																		const newObject = {
																			...selectedEmployees,
																			[employee.id]: true,
																		};
																		if (!e.target.checked)
																			delete newObject[employee.id];
																		setSelectedEmployeeList(newObject);
																	}}
																/>
																<span />
															</label>
														</td>
													</tr>
												);
											})}
											{!employeeList?.length ? (
												<tr>
													<td colSpan="2">No employees to import</td>
												</tr>
											) : null}
										</tbody>
									</table>
									<div className="pull-right">
										{selectedEmployeesLength > 0 ? (
											<>
												<button
													type="button"
													style={{ marginRight: "1rem" }}
													onClick={() => ignoreEmployees()}
													className="btn btn-danger"
												>
													Ignore {selectedEmployeesLength} Employee
													{selectedEmployeesLength > 1 ? "s" : null}
												</button>
												<button
													type="button"
													onClick={() => importEmployees()}
													className="btn btn-success"
												>
													Import {selectedEmployeesLength} Employee
													{selectedEmployeesLength > 1 ? "s" : null}
												</button>
											</>
										) : null}
										{!employeeList?.length ? (
											<button
												type="button"
												onClick={() => selectIntegration(true)}
												className="btn btn-default"
											>
												<ArrowLeftIcon className="h-6 w-6 mr-3 align-middle" />
												Go Back
											</button>
										) : null}
									</div>
								</>
							) : (
								<div className="col-xs-12">
									<label>Import From</label>
									<BuddySelector
										options={integrations}
										defaultMenuIsOpen={true}
										// menuIsOpen={true}
										onChange={(item) => {
											if (item.onSelect) {
												item.onSelect(item.value);
												return;
											}
											selectIntegration(item.value);
										}}
									/>
								</div>
							)}
							<div className="clearfix" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const { importEmployeeModal } = state.integration;

	const integrationList = Object.entries(state.integration || {})?.reduce((prev, [field, curr]) => {
		Object.values(curr || {}).map((item) => {
			if (item.name) {
				const hasImport = importEmployeeIntegrations.includes(item.name);
				const name = item.name === "Tanda" ? "Workforce" : item.name;
				prev.push({
					label: name,
					value: camelCase(String(item.name)),
					path: field,
					isDisabled: !hasImport,
					description: hasImport ? undefined : "Does not support importing employees",
					integrated: true,
				});
			}
		});
		return prev;
	}, []);

	const selected = integrationList?.find((item) => item.value === importEmployeeModal);
	const { employees, links, ignoredEmployees } = state.integration?.[selected?.path]?.[selected?.value] || {};

	const employeeList = employees?.filter((item) => {
		const ignored = ignoredEmployees?.find((ignored) => ignored?.link == item?.id);
		const imported = links?.find((link) => link?.link == item?.id);
		return !ignored && !imported;
	});
	return {
		modalStatus: importEmployeeModal,
		integrationList,
		employeeList,
		jurisdiction: state.company?.settings?.jurisdiction,
	};
};

const mapDispatchToProps = (dispatch) => ({
	openExcelImportModal: (type) => {
		dispatch(INTEGRATION_ACTIONS.toggleImportModal());
		dispatch(EMPLOYEE_ACTIONS.toggleImportModal(type));
	},
	closeModal: () => dispatch(INTEGRATION_ACTIONS.toggleImportModal()),
	selectIntegration: (integration) => dispatch(INTEGRATION_ACTIONS.toggleImportModal(integration)),
	getEmployeeLists: {
		bambooHr: () => dispatch(BAMBOO_ACTIONS.getBambooEmployees()),
		smartPension: () => dispatch(SMART_PENSION_ACTIONS.getSmartPensionEmployees()),
	},
	ignoreEmployee: {
		bambooHr: (links) => dispatch(BAMBOO_ACTIONS.ignoreEmployee(links)),
	},
	importEmployee: {
		bambooHr: (links) => dispatch(BAMBOO_ACTIONS.importEmployees(links)),
		smartPension: (links) => dispatch(SMART_PENSION_ACTIONS.importSmartPensionEmployees(links)),
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(ImportEmployeeModal);
